import { ActionHandler } from "..";
import { getUserDetails, getUserProfile } from "src/utils/api/userDetails";
import { transformUserDetailsToUserData } from "src/utils/userDetailsTransform";
import { setUserProperties } from "src/utils/redux/slices/userData";
import { addSurveyAnswers } from "@utils/redux/slices/surveyAnswers";
import { captureException } from "src/utils/error";
import {
  getInAppAutoCookie,
  getWebAuthCookie,
  getAccessToken,
} from "src/utils/authCookieParser";

/**
 * ActionHandler to fetch user details and profile from coach server and update Redux state.
 *
 * Prerequisites:
 * - User must be authenticated (have valid access token)
 *
 * Updates performed:
 * - Physical measurements → userData slice (weight, height)
 * - Demographics/identity → surveyAnswers slice (gender, sex, age, target weight)
 * - User name → surveyAnswers slice (name from profile endpoint, if available)
 * - Single backend save combining all userData updates via setUserProperties
 *
 * @param store - Redux store for state access and dispatch
 */
export const fetchAndUpdateB2BUserDetails: ActionHandler = async ({
  store,
}: Parameters<ActionHandler>[0]) => {
  // Check if user is authenticated first
  const accessToken = getAccessToken();
  if (!accessToken) {
    captureException(
      new Error("User is not authenticated"),
      "fetchAndUpdateB2BUserDetails-not-authenticated"
    );
    return;
  }

  // Get access code from multiple sources in order of priority:
  // 1. accessCode from in-app auth cookie (accessToken)
  // 2. accessCode from web auth cookie (accessToken)
  // 3. userData in Redux store
  const inAppAuthCookie = getInAppAutoCookie();
  const webAuthCookie = getWebAuthCookie();
  const { userData } = store.getState();

  const accessCode =
    inAppAuthCookie?.accessCode ||
    webAuthCookie?.accessCode ||
    userData.access_code;

  if (!accessCode) {
    captureException(
      new Error("No access code available for fetching user details"),
      "fetchAndUpdateB2BUserDetails-no-access-code"
    );
    return;
  }

  try {
    // Fetch user details and profile information
    const [userDetailsResponse, userProfileResponse] = await Promise.all([
      getUserDetails(accessCode),
      getUserProfile(accessCode),
    ]);

    // Transform the response to UserDataState and SurveyAnswersState formats
    const { userData: userDataUpdate, surveyAnswers } =
      transformUserDetailsToUserData(userDetailsResponse);

    // Add name from profile to survey answers (if available)
    const updatedSurveyAnswers = {
      ...surveyAnswers,
      ...(userProfileResponse?.name && { name: userProfileResponse.name }),
    };

    const hasUserDataUpdates = Object.keys(userDataUpdate).length > 0;
    const hasSurveyAnswers = Object.keys(updatedSurveyAnswers).length > 0;

    // Update Redux state for survey answers
    if (hasSurveyAnswers) {
      store.dispatch(addSurveyAnswers(updatedSurveyAnswers));
    }

    // Update userdata and save to backend
    if (hasUserDataUpdates || hasSurveyAnswers) {
      const combinedUserDataUpdate = {
        ...userDataUpdate,
        // Since B2B users are only in the US we can specify survey name space
        ...(hasSurveyAnswers && { personaSurveyUS: updatedSurveyAnswers }),
      };

      await store.dispatch(setUserProperties(combinedUserDataUpdate));
    }
  } catch (err) {
    captureException(err, "Failed to fetch and update user details");
  }
};
